import React, { useState } from "react"
import { useDispatch } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import CustomModal from "../basics/interface/CustomModal"
import { COLORS } from "../../utilities/Constants"
import { postDepMission } from "../../store/actions/depannageDispatchActions"
import { getLieuAffichage } from "../../utilities/depLieux"
import PointInteretSelectV2, {
	POINTS_INTERETS_V2_ATELIER,
} from "../others/PointInteretSelectV2"
import { getBasePointInteret } from "../../utilities/defaultObjects"
import { POINT_INTERET_TYPE_PARC } from "../others/PointInteretComponent"
import {
	DEP_STATE_READY_TO_DISPATCH,
	DEP_STATE_RENDEZ_VOUS,
	DEP_TYPE_TRANSPORT,
} from "../../utilities/Addons"

// appel_type « Privé » (svp/constants.py). A16 (CDC 16.10) : la livraison
// prend le type d'appel de la fiche (le serveur l'impose aussi).
const APPEL_TYPE_PRIVE = 2

// "YYYY-MM-DDTHH:mm" (input datetime-local) → "DD-MM-YYYY HH:mm" (API).
const toApiDatetime = (local) => {
	if (!local) return null
	const [d, t] = local.split("T")
	const [y, m, day] = d.split("-")
	return `${day}-${m}-${y} ${t}`
}

// Retour client 15.10 : destination pré-remplie avec l'adresse du client de
// la fiche (deux lignes libres, modifiables) — « Rue de Lyon 5 » / « 1203
// Genève » → rue + numéro / NPA + localité ; ce qui ne se découpe pas reste
// tel quel dans la rue / la localité. Revue A8 : « 12 bis / ter » compris.
export const poiDepuisAdresseClient = (dep) => {
	const poi = getBasePointInteret()
	const ligne0 = (dep.adresse_client_0 || "").trim()
	const ligne1 = (dep.adresse_client_1 || "").trim()
	if (!ligne0 && !ligne1) return poi
	const rue = ligne0.match(/^(.*\D)\s+(\d+(?:\s?(?:bis|ter|[a-zA-Z]))?)$/i)
	const npa = ligne1.match(/^(\d{4,5})\s+(.+)$/)
	return {
		...poi,
		adresse: {
			...poi.adresse,
			rue: rue ? rue[1].trim() : ligne0,
			numero: rue ? rue[2] : "",
			code_postal: npa ? npa[1] : "",
			localite: npa ? npa[2].trim() : ligne1,
		},
	}
}

// Création d'une mission de livraison depuis la fiche : véhicule/client repris
// de la fiche, départ = destination finale de la fiche (où est le véhicule),
// destination saisie ici. Sans date → « Prêt au dispatch » ; avec date → la
// mission naît planifiée (« Rendez-vous », visible dans le planning).
const DepLivraisonMissionModal = ({ dep, onClose, onCreated }) => {
	const dispatch = useDispatch()
	// Destination = point d'intérêt (même sélecteur que la fiche : adresse
	// avec autosuggest, parc, fourrière, garage, coordonnées).
	const [destinationPoi, setDestinationPoi] = useState(() =>
		poiDepuisAdresseClient(dep),
	)
	const [telephone, setTelephone] = useState(dep.telephone || "")
	const [prix, setPrix] = useState("")
	const [dateRdv, setDateRdv] = useState("") // datetime-local
	const [note, setNote] = useState("")
	const [confirm, setConfirm] = useState(false)
	const [saving, setSaving] = useState(false)

	// Même contrat que PointInteretSelectV2 attend (key/value ou objet full).
	const updateDestinationPoi = (key, value, full = false) => {
		setConfirm(false)
		setDestinationPoi((prev) => (full ? value : { ...prev, [key]: value }))
	}

	const depart = getLieuAffichage(dep.destination_finale, [])
	// GE-66 (passe 4, 🟡5) : fiche privée partie du Parc → relivraison au
	// forfait de la grille privée (même règle que est_relivraison serveur) :
	// pas de prix du bureau.
	const relivraison =
		dep.appel_type === APPEL_TYPE_PRIVE &&
		!!dep.destination_finale &&
		dep.destination_finale.id > 0 &&
		dep.destination_finale.type === POINT_INTERET_TYPE_PARC
	// POI existant sélectionné (parc/fourrière) OU adresse saisie.
	const valide =
		destinationPoi.id > 0 ||
		(destinationPoi.adresse &&
			(destinationPoi.adresse.rue || "").trim() !== "") ||
		(destinationPoi.label || "").trim() !== ""

	const creer = () => {
		if (!confirm) {
			setConfirm(true)
			return
		}
		setSaving(true)
		const payload = {
			dep: dep.id,
			est_livraison: true,
			type: DEP_TYPE_TRANSPORT,
			appel_type: dep.appel_type,
			etat: dateRdv ? DEP_STATE_RENDEZ_VOUS : DEP_STATE_READY_TO_DISPATCH,
			nom: dep.nom || "",
			prenom: dep.prenom || "",
			telephone: telephone.trim(),
			plaques: dep.plaques || "",
			marque: dep.marque || "",
			modele: dep.modele || "",
			destination_finale: destinationPoi,
			total_presume: relivraison ? 0 : parseFloat(prix) || 0,
			info_depanneur: note.trim(),
		}
		// Départ = là où est le véhicule (POI existant de la fiche).
		if (dep.destination_finale && dep.destination_finale.id)
			payload.lieu_intervention = dep.destination_finale
		if (dateRdv) {
			payload.date_rendez_vous = toApiDatetime(dateRdv)
			payload.delta_rendez_vous = 90
		}
		dispatch(postDepMission(JSON.stringify(payload)))
			.then((mission) => {
				if (mission && mission.id) onCreated(mission)
				else {
					setSaving(false)
					setConfirm(false)
				}
			})
			.catch(() => {
				setSaving(false)
				setConfirm(false)
			})
	}

	return (
		<CustomModal
			title="Créer une relivraison"
			subtitle="La mission part au dispatch"
			onClose={() => (saving ? null : onClose())}
			showCloseButton={!saving}
			buttons={[
				{
					title: "Annuler",
					styleName: "secondary",
					onClick: onClose,
					disabled: saving,
				},
				{
					title: saving ? (
						<FontAwesomeIcon icon={faSync} spin />
					) : confirm ? (
						"Confirmer ?"
					) : (
						"Créer la relivraison"
					),
					styleName: "primary",
					onClick: creer,
					disabled: saving || !valide,
				},
			]}
		>
			{/* Récap fiche (lecture seule). */}
			<div style={st.recap}>
				<div style={st.recapRow}>
					<span style={st.recapLabel}>Véhicule</span>
					<span>
						{[dep.plaques, dep.marque, dep.modele]
							.filter(Boolean)
							.join(" · ") || "—"}
					</span>
				</div>
				<div style={st.recapRow}>
					<span style={st.recapLabel}>Client</span>
					<span>
						{`${dep.nom || ""} ${dep.prenom || ""}`.trim() || "—"}
					</span>
				</div>
				<div style={st.recapRow}>
					<span style={st.recapLabel}>Départ</span>
					<span>{depart ? depart.lines.join(", ") : "—"}</span>
				</div>
			</div>

			<div style={st.field}>
				<label style={st.label}>
					Destination de la relivraison
					<span style={{ color: COLORS.danger }}> *</span>
				</label>
				<PointInteretSelectV2
					pointInteret={destinationPoi}
					updatePointInteret={updateDestinationPoi}
					list={POINTS_INTERETS_V2_ATELIER}
					inputStyle={st.input}
				/>
			</div>
			<div style={st.row2}>
				<div style={st.field}>
					<label style={st.label}>Téléphone</label>
					<input
						style={st.input}
						value={telephone}
						onChange={(e) => setTelephone(e.target.value)}
					/>
				</div>
				{relivraison ? (
					<div style={st.field}>
						<label style={st.label}>Prix</label>
						<span style={st.forfait}>
							Forfait relivraison (grille privée, calculé au
							terrain)
						</span>
					</div>
				) : (
					<div style={st.field}>
						<label style={st.label}>Prix (CHF)</label>
						<input
							style={st.input}
							type="number"
							step="any"
							value={prix}
							onFocus={(e) => e.target.select()}
							onChange={(e) => setPrix(e.target.value)}
						/>
					</div>
				)}
			</div>
			<div style={st.field}>
				<label style={st.label}>Date souhaitée (optionnel)</label>
				<input
					style={st.input}
					type="datetime-local"
					value={dateRdv}
					onChange={(e) => setDateRdv(e.target.value)}
				/>
			</div>
			<div style={st.field}>
				<label style={st.label}>Note pour le dépanneur</label>
				<textarea
					style={{
						...st.input,
						height: 64,
						padding: 10,
						resize: "vertical",
					}}
					value={note}
					onChange={(e) => setNote(e.target.value)}
				/>
			</div>
		</CustomModal>
	)
}

const st = {
	recap: {
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		padding: "10px 12px",
		marginBottom: 14,
		display: "flex",
		flexDirection: "column",
		gap: 6,
		fontSize: 13,
		color: COLORS.text,
	},
	recapRow: { display: "flex", gap: 10 },
	recapLabel: {
		flex: "0 0 70px",
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textMuted,
	},
	row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },
	forfait: {
		minHeight: 38,
		display: "flex",
		alignItems: "center",
		fontSize: 13,
		color: COLORS.textMuted,
	},
	field: { display: "flex", flexDirection: "column", marginBottom: 14 },
	label: {
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textMuted,
		marginBottom: 6,
	},
	input: {
		height: 38,
		minWidth: 0,
		padding: "0 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 14,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		boxSizing: "border-box",
	},
}

export default DepLivraisonMissionModal
